import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useRef } from 'react';
import { z } from 'zod';
import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Confetti, type ConfettiRef } from '@/components/ui/confetti';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CertificationKind } from '../../-constants';
const CompletedSchema = z.object({
  addedCertificationID: z.string()
});
export const Route = createFileRoute('/_staff/certification/add/$certificationKind/completed')({
  validateSearch: zodValidator(CompletedSchema),
  loaderDeps: ({
    search
  }) => ({
    search
  }),
  component: () => <Completed />
});
function Completed() {
  const confettiRef = useRef<ConfettiRef>(null);
  const {
    session
  } = useAuthStore();
  const certificationKind = useParams({
    from: '/_staff/certification/add/$certificationKind/completed'
  }).certificationKind as CertificationKind;
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.certification.add.form.completed'
  });
  const {
    search
  } = Route.useLoaderDeps();
  const navigate = useNavigate();
  const actionButton = (() => {
    if (certificationKind === CertificationKind.BASE) {
      return <ActionButton variant="secondary" onClick={() => {
        navigate({
          to: `/certification/$id`,
          params: {
            id: search.addedCertificationID
          }
        });
      }}>
          {t('goToBaseCertification')}
        </ActionButton>;
    }
    if (certificationKind === CertificationKind.SPECIALTY) {
      return <ActionButton variant="secondary" onClick={() => {
        navigate({
          to: `/certification/specialty-certification/$id`,
          params: {
            id: search.addedCertificationID
          }
        });
      }}>
          {t('goToSpecialtyCertification')}
        </ActionButton>;
    }
    if (certificationKind === CertificationKind.HANDLING) {
      return <ActionButton variant="secondary" onClick={() => {
        navigate({
          to: `/certification/handling-certification/$id`,
          params: {
            id: search.addedCertificationID
          }
        });
      }}>
          {t('goToHandlingCertification')}
        </ActionButton>;
    }
    return <ActionButton variant="secondary" onClick={() => {
      navigate({
        to: `/certification`,
        params: {
          id: search.addedCertificationID
        }
      });
    }}>
        {t('goToList')}
      </ActionButton>;
  })();
  return <VStack className="flex items-center justify-center">
      <Confetti ref={confettiRef} />
      <AttributeCard title={t('result')} className="w-2/4 p-4" actionButton={actionButton}>
        <VStack>
          <Text variant="h3">{t(`${certificationKind}.message`)}</Text>
          <Spacer />
          <HStack className="items-center">
            <Icon iconName="SquareCheckBig" text={t('completedBy')} />
            <Spacer />
            <Avatar className="grid size-8 place-items-center">
              <AvatarImage src={session?.person.avatarUrl} />
              <AvatarFallback>
                <Icon iconName="UserRoundIcon" />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            {session?.person?.name}
          </HStack>
        </VStack>
      </AttributeCard>
      <Spacer variant="xl" />
      <ActionButton variant="ghost" onClick={() => {
      navigate({
        to: '/certification/add'
      });
    }} icon="PlusIcon" iconColor="active">
        {t('continueAdding')}
      </ActionButton>
    </VStack>;
}